import { createElement, useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useFunnelStore } from "@/store";
import { useStepperStore } from "@/store/stepper";
import {
  useClaimCodename,
  useFunnelSession,
} from "@/features/funnel/api/use-funnel";
import { resolveStepType } from "@/features/stepper/steptypes/registry";
import { STEP_FRAME_CLASS } from "@/features/stepper/steptypes/step-shell";
import { StepperGate } from "@/features/stepper/stepper-gate";
import { TapDebug } from "@/features/stepper/tap-debug";
import { EntryGate } from "@/features/funnel/components/entry-gate";
import { arrivedWithNothing } from "@/features/funnel/components/entry";
import { genOpCode } from "@/features/funnel/lib/codenames";
import { useDailyDoors, missionIdFor } from "@/features/funnel/api/use-doors";
import { fill } from "@/features/funnel/lib/copy";
import { reportStep } from "@/features/stepper/report-step";
import { bookStepTexts } from "@/features/funnel/api/use-mission-file";
import type { Door, Tier } from "@/features/funnel/types";

export const Route = createFileRoute("/")({
  component: PlayerRoute,
});

// The gate lives here (not in the root) so the graph loads only for the
// funnel — standalone routes like /key-handoff never touch the stepper.
function PlayerRoute() {
  // Every funnel screen is exactly one viewport tall, so the document itself
  // must never scroll — on iOS Safari the body still rubber-bands otherwise.
  // Scoped to this route so pages that do scroll (mission kit) are untouched.
  useEffect(() => {
    const root = document.documentElement;
    const prev = {
      overflow: root.style.overflow,
      osb: root.style.overscrollBehavior,
    };
    root.style.overflow = "hidden";
    root.style.overscrollBehavior = "none";
    // A phone keyboard scrolls the page up to show the field, and with the
    // document pinned nothing scrolls it back — the screen is left stuck over
    // a blank strip. Whenever the keyboard goes away (the visual viewport
    // grows back, or focus leaves a field, including one disabled mid-typing),
    // put the page back. One place for every step's fields.
    const typing = () => {
      const f = document.activeElement;
      return (
        (f instanceof HTMLInputElement || f instanceof HTMLTextAreaElement) &&
        !f.disabled
      );
    };
    const reset = () => {
      if (!typing()) window.scrollTo(0, 0);
    };
    // Restore the viewport-resize behavior from the pre-October-5 build:
    // reset immediately on resize, then once more after focus leaves a field.
    // Keep the delayed reset cancellable when this route unmounts.
    let timer: ReturnType<typeof setTimeout>;
    const later = () => {
      clearTimeout(timer);
      timer = setTimeout(reset, 350);
    };
    const vv = window.visualViewport;
    vv?.addEventListener("resize", reset);
    document.addEventListener("focusout", later);
    return () => {
      clearTimeout(timer);
      vv?.removeEventListener("resize", reset);
      document.removeEventListener("focusout", later);
      root.style.overflow = prev.overflow;
      root.style.overscrollBehavior = prev.osb;
    };
  }, []);
  // The funnel is composed for a phone — a full-height desk photo, files laid
  // on it, one screen per beat — so the player is capped by ASPECT rather than
  // by a breakpoint: never wider than 0.62 of its own height. A phone is
  // narrower than that and fills the screen; an upright iPad keeps ~83% of the
  // width, which reads as full screen while the composition still holds; a
  // landscape window lands on a centred portrait panel. Orientation queries
  // could not express this — an iPad held upright is portrait but far wider
  // than the layout was built for, and the key ended up over the title.
  // `contain: layout` makes this panel the box that `fixed` overlays (the
  // well-done scene, the legend shelf) fill, not the whole window.
  // Nothing says who they are or who invited them (no key, no texted link,
  // no run here, never signed in here as an operative): the entry gate, not
  // the funnel. Not the session's number: signing in with any number puts
  // one on it, and that alone must not let someone in without a key.
  const [gate] = useState(arrivedWithNothing);
  return (
    <div data-sm-player className={STEP_FRAME_CLASS}>
      {gate ? (
        <EntryGate />
      ) : (
        <StepperGate>
          <StepperPlayer />
        </StepperGate>
      )}
      <TapDebug />
    </div>
  );
}

const TIERS: Tier[] = ["beginner", "challenging", "improbable"];

/**
 * The stepper player. Renders the current web step with its Glow template
 * (StepTypes/*), populated from the step's JSON options. `dispatch` walks the
 * graph — and carries the product side effects the pure templates don't:
 * recording the door/handler pick and, on Difficulty, launching the SMS (the
 * tap IS the acceptance) before advancing to Delivered.
 */
/**
 * Each screen is a browser history entry (`?step=<StepId>`), so the back
 * and forward buttons walk the steps. The address follows the step (pushed on
 * a move, replaced on arrival); a back/forward press moves the step to match.
 */
function useStepInHistory(current: string) {
  const goTo = useStepperStore((s) => s.goTo);
  const graph = useStepperStore((s) => s.graph);
  // The address shows the step's id (ai.steps.StepId), its name when it has none.
  const id = graph?.steps[current]?.stepId;
  const key = id != null ? String(id) : current;
  useEffect(() => {
    if (!key) return;
    const url = new URL(window.location.href);
    const shown = url.searchParams.get("step");
    if (shown === key) return;
    url.searchParams.set("step", key);
    window.history[shown ? "pushState" : "replaceState"](null, "", url);
  }, [key]);
  useEffect(() => {
    const onPop = () => {
      const shown = new URL(window.location.href).searchParams.get("step");
      if (!shown) return;
      const steps = useStepperStore.getState().graph?.steps ?? {};
      const name =
        Object.values(steps).find((s) => String(s.stepId) === shown)
          ?.stepName ?? shown;
      goTo(name, true);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [goTo]);
}

function StepperPlayer() {
  const graph = useStepperStore((s) => s.graph);
  const currentStepName = useStepperStore((s) => s.currentStepName);
  const storeDispatch = useStepperStore((s) => s.dispatch);
  const adventureId = useStepperStore((s) => s.adventureId);
  const funnel = useFunnelStore();
  const { data: session } = useFunnelSession();
  const { data: doors } = useDailyDoors();
  const claimCodename = useClaimCodename();
  // Difficulty tap awaits a claimed codename; ignore re-taps while in flight.
  const acceptingRef = useRef(false);
  useStepInHistory(currentStepName);

  const step = graph?.steps[currentStepName] ?? null;
  if (!step) return null;

  // Fill copy placeholders from run state: {referrer} = the KEY owner's name
  // (resolved from the scanned code), {handler} = the chosen handler.
  const referrer = funnel.referrerName || session?.referrerName || "Ashley";
  // The open case (WebSmCase) is the second half of the Difficulty step: it
  // reads that step's options for what it does not set itself, and its
  // accept is recorded on that step's row (texts and stepper look there).
  const difficultyStep =
    Object.values(graph?.steps ?? {}).find(
      (s) => s.stepType === "WebSmDifficulty",
    ) ?? null;
  const getOpt = (name: string): string =>
    fill(
      step.options[name] ??
        (step.stepType === "WebSmCase"
          ? difficultyStep?.options[name]
          : undefined) ??
        "",
      {
        referrer,
        handler: funnel.handler ?? undefined,
      },
    );
  const clickIndex = (eventV: string) => Number(eventV.slice(5)) || 1;

  const dispatch = (eventV: string) => {
    const isClick = eventV.startsWith("CLICK");

    // Record the pick so downstream steps (and the SMS) have it.
    if (isClick && step.stepType === "WebSmDoors") {
      const door: Door = clickIndex(eventV) === 1 ? "love" : "stranger";
      funnel.pickDoor({ door, opCode: genOpCode() });
      // A doors step that hands out the case itself (CaseTier — the first
      // mission's: no case desk, the Standard case is ready for them).
      const caseTier = getOpt("CaseTier") as Tier;
      if (TIERS.includes(caseTier)) funnel.openCase(caseTier);
      reportStep(adventureId, step, { Door: door });
    } else if (isClick && step.stepType === "WebSmHandler") {
      const handlerName =
        getOpt(`Handler${clickIndex(eventV)}Name`) || "Veronica";
      funnel.pickHandler(handlerName);
      reportStep(adventureId, step, { Handler: handlerName });
      // The codename is NOT claimed here. A codename names a mission, so it is
      // claimed on the difficulty tap — the moment a mission actually exists.
      // Claiming at the handler pick burned a name before the choice and gave
      // every file the same one to display.
    }

    // The case: accepting the mission it showed.
    if (isClick && step.stepType === "WebSmCase") {
      // Back from the live mission, the case shows the mission already
      // accepted: going on is not a second accept (no new clock, no second
      // report, no second texts) — just on to it again.
      const accepted = useFunnelStore.getState().missionId;
      if (useStepperStore.getState().cameBack && accepted) {
        storeDispatch(eventV);
        return;
      }
      if (acceptingRef.current) return;
      acceptingRef.current = true;

      const n = clickIndex(eventV);
      const tier = TIERS[n - 1] ?? "beginner";

      void (async () => {
        // A codename for the mission (the stepper reads it on an inbound
        // text). Best-effort: nothing on the web shows it, so a pool with no
        // free names must not stop anyone accepting.
        if (!useFunnelStore.getState().codeName) {
          try {
            // `difficulty` rides along so the claimed row records which
            // mission the name belongs to.
            const result = await claimCodename.mutateAsync({
              difficulty: tier,
            });
            if (result?.codeName) funnel.setCodeName(result.codeName);
          } catch {
            /* accepted without one */
          }
        }

        const missionId = missionIdFor(doors, funnel.door, tier);
        funnel.acceptMission({ tier, missionId });
        // This screen's own answer, on this screen's row. MissionID rides
        // along so stepper loads the exact mission that was on the door
        // instead of re-deriving it and disagreeing with the label shown.
        const owner = difficultyStep ?? step;
        reportStep(adventureId, owner, {
          Difficulty: tier,
          MissionID: String(missionId ?? ""),
        });

        // The mission was already shown on the file: accepting it is the
        // whole handoff (never a code texted to the handler), and the texts
        // that follow are booked here.
        void bookStepTexts(adventureId, owner.stepName, tier, missionId);
        acceptingRef.current = false;
        storeDispatch(eventV);
      })();
      return;
    }

    // Delivered replay resets the run before the graph walks back to Start.
    if (step.stepType === "WebSmDelivered" && eventV === "CLICK1") {
      funnel.resetFunnel();
    }

    storeDispatch(eventV);
  };

  const Template = resolveStepType(step.stepType);
  if (!Template) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#0a0809] font-mono text-[#e6d3a4]">
        No renderer for step type “{step.stepType}”.
      </div>
    );
  }
  // createElement (not <Template/>) — the renderer is resolved dynamically
  // from the step type, not a component declared in this scope.
  //
  // `dispatch` closes over acceptingRef, but only ever runs from a click
  // handler, never during render. The ref is the right primitive for a
  // double-submit guard: useState would not have updated yet on a second tap
  // in the same tick, which is exactly the case it exists to stop.
  // keyed by step: two steps with the same template (Order and BigOne are both
  // WebSmOrder) must not share one screen's state when moving between them
  // (a mission's chain of steps keeps one instance, so the deck's motion
  // plays across the branch from one step to the next)
  const key =
    step.stepType === "WebSmMissionStep" ? "mission-step" : step.stepName;
  // eslint-disable-next-line react-hooks/refs
  return createElement(Template, { key, getOpt, dispatch });
}
